/**
 * /finance/bills — AP / Bills
 * Derives vendor bill exposure from canonical PurchaseOrders in
 * Confirmed / Delivered status. Read-only; payment writes belong to
 * a future procurement-finance bridge (Phase 6B groundwork exists).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useVendorPO } from "@/data/VendorPOContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import {
  buildBillsFromPOs,
  rollupBillsByVendor,
  type BillStatus,
} from "@/data/finance/financeWorkbenchSelectors";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;

const STATUS_TONE: Record<BillStatus, string> = {
  overdue: "text-destructive font-medium",
  delivered: "text-warning",
  confirmed: "text-muted-foreground",
};
const STATUS_LABEL: Record<BillStatus, string> = {
  overdue: "Overdue",
  delivered: "Delivered · Net-30",
  confirmed: "Confirmed · Net-45",
};

export default function BillsPage() {
  const { purchaseOrders } = useVendorPO();
  const fin$ = useFinancialPermissions();
  const [view, setView] = useState<"by-bill" | "by-vendor">("by-bill");
  const [statusFilter, setStatusFilter] = useState<BillStatus | "all">("all");

  const bills = useMemo(() => buildBillsFromPOs(purchaseOrders), [purchaseOrders]);
  const vendorRollup = useMemo(() => rollupBillsByVendor(bills), [bills]);

  const filteredBills = useMemo(() => {
    if (statusFilter === "all") return bills;
    return bills.filter((b) => b.status === statusFilter);
  }, [bills, statusFilter]);

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
        </div>
      </CockpitPage>
    );
  }

  const totalDue = bills.reduce((s, b) => s + b.amount, 0);
  const overdueTotal = bills.filter((b) => b.status === "overdue").reduce((s, b) => s + b.amount, 0);
  const due7d = bills.filter((b) => b.dueInDays >= 0 && b.dueInDays <= 7).reduce((s, b) => s + b.amount, 0);

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Bills · Accounts Payable</h1>
        <p className="text-[12px] text-muted-foreground mt-1">
          Derived from confirmed and delivered POs · {bills.length} open · {fmtCompact(totalDue)} owed
        </p>
      </header>

      <FinanceNavStrip />

      {/* Reconciliation source-of-truth banner */}
      <div className="text-[11px] border border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400 px-3 py-2 rounded-sm flex items-start gap-2">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          <strong>Operational view.</strong> Figures derived from purchase orders for cash-planning. Bookkeepable AP balances and aging live in the
          {" "}<Link to="/accounting/ap" className="underline hover:text-amber-800 dark:hover:text-amber-300">General Ledger AP module</Link> — reconcile there before reporting.
        </span>
      </div>


      {/* AP summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-border/60 divide-x divide-y md:divide-y-0 divide-border/60 bg-background">
        <button onClick={() => setStatusFilter("all")} className={`px-4 py-3 text-left ${statusFilter === "all" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Total open</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtCompact(totalDue)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{bills.length} bills</p>
        </button>
        <button onClick={() => setStatusFilter("overdue")} className={`px-4 py-3 text-left ${statusFilter === "overdue" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-destructive font-medium">Overdue</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1 text-destructive">{fmtCompact(overdueTotal)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{bills.filter((b) => b.status === "overdue").length} bills</p>
        </button>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Due this week</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtCompact(due7d)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{bills.filter((b) => b.dueInDays >= 0 && b.dueInDays <= 7).length} bills</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Vendors</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{vendorRollup.length}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{vendorRollup.filter((v) => v.overdueCount > 0).length} with overdue</p>
        </div>
      </div>

      {/* View switcher */}
      <div className="flex items-center gap-1 text-[12px]">
        {(["by-bill", "by-vendor"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-3 py-1 border ${view === v ? "border-primary/30 bg-primary/10 text-primary font-medium" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
          >
            {v === "by-bill" ? "By bill" : "By vendor"}
          </button>
        ))}
      </div>

      {view === "by-bill" ? (
        <div className="bg-background border border-border/60">
          <div className="grid grid-cols-[120px_1fr_120px_110px_110px_80px] gap-4 px-4 py-2.5 border-b border-border/60 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
            <span>PO #</span>
            <span>Vendor · Job</span>
            <span>Status</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Due</span>
            <span className="text-right">Age</span>
          </div>
          {filteredBills.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No bills match.</p>
          ) : (
            filteredBills.map((b) => (
              <div key={b.poId} className="grid grid-cols-[120px_1fr_120px_110px_110px_80px] gap-4 px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
                <span className="text-[12px] text-muted-foreground font-mono">{b.poNumber}</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground truncate">{b.vendorName}</p>
                  <Link to={`/jobs/${b.jobId}`} className="text-[11px] text-muted-foreground hover:text-primary truncate block">
                    {b.jobLabel || b.jobId}
                  </Link>
                </div>
                <span className={`text-[11px] ${STATUS_TONE[b.status]}`}>{STATUS_LABEL[b.status]}</span>
                <span className="text-right text-[13px] font-semibold tabular-nums">{fmt(b.amount)}</span>
                <span className={`text-right text-[12px] tabular-nums ${b.dueInDays < 0 ? "text-destructive font-medium" : b.dueInDays <= 7 ? "text-warning" : "text-muted-foreground"}`}>
                  {b.dueInDays < 0 ? `${Math.abs(b.dueInDays)}d late` : b.dueInDays === 0 ? "today" : `in ${b.dueInDays}d`}
                </span>
                <span className="text-right text-[11px] text-muted-foreground tabular-nums">{b.ageDays}d</span>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-background border border-border/60">
          <div className="grid grid-cols-[1fr_100px_120px_120px_120px] gap-4 px-4 py-2.5 border-b border-border/60 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
            <span>Vendor</span>
            <span className="text-right">Open bills</span>
            <span className="text-right">Total due</span>
            <span className="text-right">Overdue</span>
            <span className="text-right">Next due</span>
          </div>
          {vendorRollup.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No open vendor bills.</p>
          ) : (
            vendorRollup.map((v) => (
              <div key={v.vendorName} className="grid grid-cols-[1fr_100px_120px_120px_120px] gap-4 px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
                <p className="text-[13px] font-medium text-foreground truncate">{v.vendorName}</p>
                <span className="text-right text-[13px] tabular-nums">{v.openBills}</span>
                <span className="text-right text-[13px] font-semibold tabular-nums">{fmt(v.totalDue)}</span>
                <span className={`text-right text-[12px] tabular-nums ${v.overdueAmount > 0 ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                  {v.overdueAmount > 0 ? `${fmt(v.overdueAmount)} (${v.overdueCount})` : "—"}
                </span>
                <span className={`text-right text-[12px] tabular-nums ${v.nextDueInDays < 0 ? "text-destructive" : v.nextDueInDays <= 7 ? "text-warning" : "text-muted-foreground"}`}>
                  {v.nextDueInDays === Infinity ? "—" : v.nextDueInDays < 0 ? `${Math.abs(v.nextDueInDays)}d late` : v.nextDueInDays === 0 ? "today" : `in ${v.nextDueInDays}d`}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </CockpitPage>
  );
}
